import { useEffect, useMemo, useState, type UIEvent } from 'react';
import { Autocomplete, TextField, Checkbox, FormControlLabel, Box, Typography } from '@mui/material';
import { FieldValue } from '../../../../../types/QueryBuilder';
import { useLanguage } from '../../../../../context/LanguageContext';

const normalizeForSearch = (value: string): string =>
    value.trim().toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');

interface SelectEditorProps {
    ruleId: string;
    value: string | string[];
    values: FieldValue[];
    isMultiSelect?: boolean;
    onChange: (value: string | string[]) => void;
}

const SELECT_ALL_LABEL = 'Tout sélectionner';

const MULTISELECT_OPTION_HEIGHT = 36;
const MULTISELECT_VISIBLE_ROWS = 5;
// Options are rendered a batch at a time and grown on scroll instead of all at once,
// since some fields (e.g. stores) carry thousands of options and rendering them all froze the UI.
const MULTISELECT_BATCH_SIZE = 5;
const SCROLL_LOAD_THRESHOLD_PX = 48;

function SelectEditor({ ruleId, value, values, isMultiSelect = false, onChange }: SelectEditorProps) {
    const { t } = useLanguage();
    const selectedValues = useMemo(() => (Array.isArray(value) ? value : []), [value]);
    const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);
    const [visibleCount, setVisibleCount] = useState(MULTISELECT_BATCH_SIZE);
    const [search, setSearch] = useState('');

    useEffect(() => {
        setVisibleCount(MULTISELECT_BATCH_SIZE);
    }, [values]);

    useEffect(() => {
        setVisibleCount(MULTISELECT_BATCH_SIZE);
    }, [search]);

    const filteredValues = useMemo(() => {
        const query = normalizeForSearch(search);
        if (!query) return values;
        return values.filter(option => normalizeForSearch(option.label).includes(query));
    }, [values, search]);

    if (isMultiSelect) {
        const allSelected = values.length > 0 && selectedValues.length === values.length;

        const toggleOption = (optionName: string, checked: boolean) => {
            const newValues = checked
                ? [...selectedValues, optionName]
                : selectedValues.filter(v => v !== optionName);
            onChange(newValues);
        };

        const toggleSelectAll = () => {
            onChange(allSelected ? [] : values.map(option => option.name));
        };

        const handleScroll = (e: UIEvent<HTMLDivElement>) => {
            const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
            if (scrollHeight - scrollTop - clientHeight < SCROLL_LOAD_THRESHOLD_PX) {
                setVisibleCount(count => Math.min(count + MULTISELECT_BATCH_SIZE, filteredValues.length));
            }
        };

        const optionRowSx = { display: 'flex', height: MULTISELECT_OPTION_HEIGHT, minHeight: MULTISELECT_OPTION_HEIGHT, m: 0, px: 1 };
        const visibleValues = filteredValues.slice(0, visibleCount);

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {values.length > 0 && (
                    <Typography variant="caption" sx={{ color: '#495057', fontWeight: 600 }}>
                        {t('pages.clients.queryBuilder.simple.selectedCount').replace('{count}', String(selectedValues.length))}
                    </Typography>
                )}
                {values.length > 0 && (
                    <TextField
                        size="small"
                        placeholder={t('pages.clients.queryBuilder.searchPlaceholder')}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                )}
                <Box
                    onScroll={handleScroll}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        maxHeight: MULTISELECT_OPTION_HEIGHT * MULTISELECT_VISIBLE_ROWS,
                        overflowY: 'auto',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        bgcolor: 'background.paper',
                    }}
                >
                    {!search && values.length > 0 && (
                        <FormControlLabel
                            sx={optionRowSx}
                            control={
                                <Checkbox
                                    checked={allSelected}
                                    onChange={toggleSelectAll}
                                    size="small"
                                />
                            }
                            label={SELECT_ALL_LABEL}
                        />
                    )}
                    {visibleValues.length === 0 ? (
                        <Typography variant="body2" sx={{ color: '#6c757d', px: 1, py: 1 }}>
                            {t('pages.clients.queryBuilder.simple.noOptionsFound')}
                        </Typography>
                    ) : (
                        visibleValues.map((option, idx) => (
                            <FormControlLabel
                                key={`${ruleId}-multiselect-${idx}`}
                                sx={optionRowSx}
                                control={
                                    <Checkbox
                                        checked={selectedSet.has(option.name)}
                                        onChange={(e) => toggleOption(option.name, e.target.checked)}
                                        size="small"
                                    />
                                }
                                label={option.label}
                            />
                        ))
                    )}
                </Box>
            </Box>
        );
    }

    const selectedOption = values.find(option => option.name === value) || null;

    return (
        <Autocomplete
            fullWidth
            size="small"
            id={`${ruleId}-select`}
            options={values}
            value={selectedOption}
            onChange={(_e, newValue) => onChange(newValue ? newValue.name : '')}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, val) => option.name === val.name}
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder="Sélectionner..."
                    inputProps={{ ...params.inputProps, autoComplete: 'off' }}
                />
            )}
            ListboxProps={{ style: { maxHeight: 300 } }}
            noOptionsText="Aucune option trouvée"
        />
    );
};

export default SelectEditor;
